import { useNostr } from '@nostrify/react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Color Moment kind (NIP-XX)
 */
export const COLOR_MOMENT_KIND = 3367;

/**
 * Validates that an event is a proper color moment
 */
function validateColorMoment(event: NostrEvent): boolean {
  // Must be correct kind
  if (event.kind !== COLOR_MOMENT_KIND) return false;

  // Must have at least 3 color tags, max 6
  const colorTags = event.tags.filter(([name]) => name === 'c');
  if (colorTags.length < 3 || colorTags.length > 6) return false;

  // Validate hex color format
  const hexColorRegex = /^#[0-9A-F]{6}$/i;
  return colorTags.every(([, color]) => hexColorRegex.test(color));
}

export type LayoutMode = 'horizontal' | 'grid' | 'vertical' | 'star' | 'checkerboard' | 'diagonalStripes';

export interface ColorMoment {
  event: NostrEvent;
  colors: string[];
  emoji?: string;
  layout?: LayoutMode;
  name?: string;
}

/**
 * Transform a single event to a ColorMoment object (or null if invalid)
 */
export function transformToColorMoment(event: NostrEvent): ColorMoment | null {
  if (!validateColorMoment(event)) return null;

  const colors = event.tags
    .filter(([tagName]) => tagName === 'c')
    .map(([, color]) => color);

  const layoutTag = event.tags.find(([tagName]) => tagName === 'layout');
  const layout = layoutTag?.[1] as LayoutMode | undefined;

  const nameTag = event.tags.find(([tagName]) => tagName === 'name');
  const name = nameTag?.[1];

  return {
    event,
    colors,
    emoji: event.content || undefined,
    layout: layout && ['horizontal', 'grid', 'vertical', 'star', 'checkerboard', 'diagonalStripes'].includes(layout) ? layout : undefined,
    name: name || undefined,
  };
}

/**
 * Transform events to ColorMoment objects
 */
function transformToColorMoments(events: NostrEvent[]): ColorMoment[] {
  return events
    .filter(validateColorMoment)
    .map((event): ColorMoment => {
      const colors = event.tags
        .filter(([tagName]) => tagName === 'c')
        .map(([, color]) => color);
      
      const layoutTag = event.tags.find(([tagName]) => tagName === 'layout');
      const layout = layoutTag?.[1] as LayoutMode | undefined;
      
      const nameTag = event.tags.find(([tagName]) => tagName === 'name');
      const name = nameTag?.[1];

      return {
        event,
        colors,
        emoji: event.content || undefined,
        layout: layout && ['horizontal', 'grid', 'vertical', 'star', 'checkerboard', 'diagonalStripes'].includes(layout) ? layout : undefined,
        name: name || undefined,
      };
    })
    .sort((a, b) => b.event.created_at - a.event.created_at);
}

/**
 * Hook to fetch color moments from Nostr
 */
export function useColorMoments(limit = 50) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['color-moments', limit],
    queryFn: async () => {
      const events = await nostr.query([
        {
          kinds: [COLOR_MOMENT_KIND],
          limit,
        },
      ]);

      return transformToColorMoments(events);
    },
  });
}

/**
 * Hook to fetch color moments by a specific user
 */
export function useUserColorMoments(pubkey: string | undefined, limit = 50) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['user-color-moments', pubkey, limit],
    queryFn: async () => {
      if (!pubkey) return [];
      
      const events = await nostr.query([
        {
          kinds: [COLOR_MOMENT_KIND],
          authors: [pubkey],
          limit,
        },
      ]);

      return transformToColorMoments(events);
    },
    enabled: !!pubkey,
  });
}

/**
 * Hook to fetch color moments from users the current user follows
 */
export function useFollowingColorMoments(userPubkey: string | undefined, limit = 50) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['following-color-moments', userPubkey, limit],
    queryFn: async () => {
      if (!userPubkey) return [];
      
      // Fetch the user's contact list (kind 3)
      const contactEvents = await nostr.query([
        {
          kinds: [3],
          authors: [userPubkey],
          limit: 1,
        },
      ]);

      if (contactEvents.length === 0) return [];

      // Extract followed pubkeys from p tags
      const followedPubkeys = contactEvents[0].tags
        .filter(([name]) => name === 'p')
        .map(([, pubkey]) => pubkey);

      if (followedPubkeys.length === 0) return [];

      // Fetch color moments from followed users
      const events = await nostr.query([
        {
          kinds: [COLOR_MOMENT_KIND],
          authors: followedPubkeys,
          limit,
        },
      ]);

      return transformToColorMoments(events);
    },
    enabled: !!userPubkey,
  });
}

const PAGE_SIZE = 20;

/**
 * Hook to fetch color moments with infinite scroll pagination
 */
export function useColorMomentsInfinite() {
  const { nostr } = useNostr();

  return useInfiniteQuery({
    queryKey: ['color-moments-infinite'],
    queryFn: async ({ pageParam }) => {
      const filter: Record<string, unknown> = {
        kinds: [COLOR_MOMENT_KIND],
        limit: PAGE_SIZE,
      };
      if (pageParam) {
        filter.until = pageParam;
      }

      const events = await nostr.query(
        [filter as import('@nostrify/nostrify').NostrFilter],
      );

      return transformToColorMoments(events);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      const oldest = lastPage[lastPage.length - 1];
      return oldest ? oldest.event.created_at - 1 : undefined;
    },
    initialPageParam: undefined as number | undefined,
  });
}

/**
 * Hook to fetch color moments from followed users with infinite scroll pagination
 */
export function useFollowingColorMomentsInfinite(userPubkey: string | undefined, enabled = true) {
  const { nostr } = useNostr();

  // First fetch the contact list
  const { data: followedPubkeys } = useQuery({
    queryKey: ['followed-pubkeys', userPubkey],
    queryFn: async () => {
      if (!userPubkey) return [];
      const contactEvents = await nostr.query([
        { kinds: [3], authors: [userPubkey], limit: 1 },
      ]);
      if (contactEvents.length === 0) return [];
      return contactEvents[0].tags
        .filter(([name]) => name === 'p')
        .map(([, pk]) => pk);
    },
    enabled: !!userPubkey && enabled,
  });

  return useInfiniteQuery({
    queryKey: ['following-moments-infinite', userPubkey],
    queryFn: async ({ pageParam }) => {
      if (!followedPubkeys || followedPubkeys.length === 0) return [];

      const filter: Record<string, unknown> = {
        kinds: [COLOR_MOMENT_KIND],
        authors: followedPubkeys,
        limit: PAGE_SIZE,
      };
      if (pageParam) {
        filter.until = pageParam;
      }

      const events = await nostr.query(
        [filter as import('@nostrify/nostrify').NostrFilter],
      );

      return transformToColorMoments(events);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      const oldest = lastPage[lastPage.length - 1];
      return oldest ? oldest.event.created_at - 1 : undefined;
    },
    initialPageParam: undefined as number | undefined,
    enabled: enabled && !!userPubkey && !!followedPubkeys && followedPubkeys.length > 0,
  });
}

/**
 * Hook to fetch cherished (bookmarked) color moments for current user
 */
export function useCherishedMoments(userPubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['cherished-hues', userPubkey],
    queryFn: async () => {
      if (!userPubkey) return [];
      
      // Fetch the user's bookmark list
      const bookmarkEvents = await nostr.query([
        {
          kinds: [30003],
          authors: [userPubkey],
          '#d': ['cherished-hues'],
          limit: 1,
        },
      ]);

      if (bookmarkEvents.length === 0) return [];

      // Get all bookmarked event IDs
      const bookmarkedIds = bookmarkEvents[0].tags
        .filter(([name]) => name === 'e')
        .map(([, id]) => id);

      if (bookmarkedIds.length === 0) return [];

      // Fetch the actual color moment events
      const events = await nostr.query([
        {
          kinds: [COLOR_MOMENT_KIND],
          ids: bookmarkedIds,
        },
      ]);

      return transformToColorMoments(events);
    },
    enabled: !!userPubkey,
  });
}
